import React from "react";
import Helmet from "react-helmet";
import { isExperienceEditorActive, dataApi } from "@sitecore-jss/sitecore-jss-react";
import SitecoreContextFactory from "./lib/SitecoreContextFactory";
import { dataFetcher } from "./dataFetcher";
import config from "./temp/config";
import LayoutFactory from "./LayoutFactory.js";
import NotFound from "./NotFound";
import { storeValidationMessages } from "./components/InputFields/validator/validators";
import { storeEnumValues } from "./Enums/enumValues";
import TagManager from "react-gtm-module";
import { PORTALTHIRD_BASE_URL } from "./constants/constants";
// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.
let ssrInitialState = null;
const cookieScriptCode = `function OptanonWrapper() {                
                OneTrust.OnConsentChanged(function (e) {
                    const sitecoreAnalyticsCookie = 'C0003';
                    const analyticsCookieEnabled = e.detail.includes(sitecoreAnalyticsCookie);                   
                    const cookieDate = new Date;
                    cookieDate.setFullYear(cookieDate.getFullYear() + 1);
                    const cookieValue = "XDB=" + analyticsCookieEnabled + "; expires = " + cookieDate.toUTCString() + "; domain = " + window.location.host + "; path = /";
                    document.cookie = "DelaCookie=" + cookieValue;
                    //Trigger current page get request for xDB
                    if(analyticsCookieEnabled)
                    {
                      fetch(window.location.href);
                    }
                });
            }`;

export default class RouteHandler extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: true,
      routeData: ssrInitialState, // null when client-side rendering
      defaultLanguage: config.defaultLanguage,
    };

    if (ssrInitialState && ssrInitialState.sitecore && ssrInitialState.sitecore.route) {
      // set the initial sitecore context data if we got SSR initial state
      SitecoreContextFactory.setSitecoreContext({
        route: ssrInitialState.sitecore.route,
        itemId: ssrInitialState.sitecore.route.itemId,
        ...ssrInitialState.sitecore.context,
      });
      const context = ssrInitialState.sitecore.context;
      // SSR rendering for EnumValues and ValidationMessages
      if (context.staticData) {
        storeEnumValues(context.staticData.enums);
        storeValidationMessages(context.staticData.validationMessages);
      }
      // Push google analytics, only on clientside rendering
      if (typeof window !== "undefined") {
        pushGtmPageRouteEvent(context);
      }
    }

    // route data from react-router - if route was resolved, it's not a 404
    if (props.route !== null) {
      this.state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (ssrInitialState && (!ssrInitialState.sitecore || !ssrInitialState.sitecore.route)) {
      this.state.notFound = true;
    }

    // if we have an SSR state, and that state has language data, set the current language
    // (this makes the language of content follow the Sitecore context language cookie)
    // note that a route-based language (i.e. /de-DE) will override this default; this is for home.
    if (ssrInitialState && ssrInitialState.context && ssrInitialState.context.language) {
      this.state.defaultLanguage = ssrInitialState.context.language;
    }

    // once we initialize the route handler, we've "used up" the SSR data,
    // if it existed, so we want to clear it now that it's in react state.
    // future route changes that might destroy/remount this component should ignore any SSR data.
    // EXCEPTION: Unless we are still SSR-ing. Because SSR can re-render the component twice
    // We test for SSR by checking for Node-specific process.env variable.
    if (typeof window !== "undefined") {
      ssrInitialState = null;
    }

    this.componentIsMounted = false;
    this.languageIsChanging = false;
  }

  componentDidMount() {
    // if no existing routeData is present (from SSR), get Layout Service fetching the route data
    if (!this.state.routeData) {
      this.updateRouteData();
    }

    this.componentIsMounted = true;
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateRouteData() {
    let sitecoreRoutePath = this.props.route.match.params.sitecoreRoute || "/";
    if (!sitecoreRoutePath.startsWith("/")) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    const language = this.props.route.match.params.lang || this.state.defaultLanguage;

    // get the route data for the new route
    getRouteData(sitecoreRoutePath, language)
      .then((routeData) => {
        if (routeData !== null && routeData.sitecore && routeData.sitecore.route) {
          // set the sitecore context data and push the new route
          SitecoreContextFactory.setSitecoreContext({
            route: routeData.sitecore.route,
            itemId: routeData.sitecore.route.itemId,
            ...routeData.sitecore.context,
          });
          this.setState({ routeData, notFound: false });
        } else {
          this.setState({ routeData, notFound: true });
        }

        return routeData;
      })
      .then((routeData) => {
        pushGtmPageRouteEvent(routeData.sitecore.context);
      });
  }

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.route.match.url;
    const newRoute = this.props.route.match.url;

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute) {
      return;
    }

    // if in experience editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isExperienceEditorActive()) {
      window.location.assign(newRoute);
      return;
    }
    this.updateRouteData();
  }

  render() {
    const { notFound, routeData } = this.state;
    const cookieEnabled =
      routeData?.sitecore?.context?.app?.cookieScriptUrl && routeData?.sitecore?.context?.app?.cookieScriptID;
    // no route data for the current route in Sitecore - show not found component.
    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    if (notFound) {
      return (
        <React.Fragment>
          <Helmet>
            <title>PortaalDerden - Page not found</title>
            <link rel="manifest" href={`/${PORTALTHIRD_BASE_URL}/manifest.json`}></link>
            {cookieEnabled && (
              <script
                src={routeData.sitecore.context.app.cookieScriptUrl}
                data-domain-script={routeData.sitecore.context.app.cookieScriptID}
                charset="UTF-8"
                type="text/javascript"
              />
            )}
            {cookieEnabled && <script type="text/javascript">{cookieScriptCode}</script>}
          </Helmet>
          <NotFound context={routeData.sitecore && routeData.sitecore.context} />
        </React.Fragment>
      );
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!routeData || this.languageIsChanging) {
      return null;
    }

    const Layout = LayoutFactory.resolveLayout(routeData.sitecore.route);

    return (
      <>
        <Helmet>
          {cookieEnabled && (
            <script
              src={routeData.sitecore.context.app.cookieScriptUrl}
              data-domain-script={routeData.sitecore.context.app.cookieScriptID}
              charset="UTF-8"
              type="text/javascript"
            ></script>
          )}
          {cookieEnabled && <script type="text/javascript">{cookieScriptCode}</script>}
        </Helmet>
        <Layout route={routeData.sitecore.route} />
      </>
    );
  }
}

/**
 * Sets the initial state provided by server-side rendering.
 * Setting this state will bypass initial route data fetch calls.
 * @param {object} ssrState
 */
export function setServerSideRenderingState(ssrState) {
  ssrInitialState = ssrState;
}

/**
 * Gets route data from Sitecore. This data is used to construct the component layout for a JSS route.
 * @param {string} route Route path to get data for (e.g. /about)
 * @param {string} language Language to get route data in (content language, e.g. 'en')
 */
function getRouteData(route, language) {
  // Workaround for sitecore, if the route equals the virtualFolder sitecore can't resolve the path. So override it with / so the content can be downloaded
  if (route === config.virtualFolder) {
    route = "/";
  }

  const fetchOptions = {
    layoutServiceConfig: { host: config.sitecoreApiHost },
    querystringParams: {
      sc_lang: language,
      sc_apikey: config.sitecoreApiKey,
      sc_site: config.jssAppName,
      isClient: true,
    },
    fetcher: dataFetcher,
  };

  return dataApi.fetchRouteData(route, fetchOptions).catch((error) => {
    if (error.response && error.response.status === 404 && error.response.data) {
      return error.response.data;
    }

    console.error("Route data fetch error", error, error.response);

    return null;
  });
}

function pushGtmPageRouteEvent(context) {
  // Goolge Tag Manager
  if (context.app.gtmId) {
    TagManager.initialize({
      gtmId: context.app.gtmId,
      dataLayer: {
        event: "pageview",
        page: window.location.pathname,
      },
    });
  }
}
